import type { NarrationLine } from './types';
import {
  type VoiceId,
  type VoiceManifest,
  CHILD_VOICES,
  EMPTY_VOICE_MANIFEST,
  availableVoices,
  pickVoice,
  pickCountingVoice,
  pickFuelVoice,
  voicePath,
  parseVoiceManifest,
} from './voices';

/**
 * Loads narration.json (single source of truth) and plays each line's audio.
 * Captions render from the SAME `text` field so narration and captions cannot
 * diverge (PRP §Audio & Captions).
 *
 * No runtime API call: audio files are bundled in /public/audio and fetched
 * from the same origin only. Audio is never attempted before the start
 * gesture (PRP AC-02) — the activity is responsible for constructing this
 * object only after the start tap.
 */

export interface NarrationOptions {
  /** Called with the current line's text whenever audio plays. */
  onCaption: (text: string, id: string) => void;
  /** Called when audio for a line ends naturally. */
  onLineEnded?: (id: string) => void;
  /** Initial mute state (e.g. restored from localStorage). */
  initialMuted?: boolean;
  /** Custom random generator (default Math.random). */
  random?: () => number;
  /** Called when a line is actually started (after the voice is chosen). */
  onVoice?: (id: string, voice: VoiceId) => void;
}

export class Narration {
  private lines = new Map<string, NarrationLine>();
  private audio = new Map<string, HTMLAudioElement>();
  private childAudio = new Map<string, HTMLAudioElement>();
  private failedChildElements = new Set<HTMLAudioElement>();
  private current: HTMLAudioElement | null = null;
  private muted: boolean;
  /**
   * Serialised playback queue. Every line and standalone chime runs through
   * it, so near-simultaneous placements can never overlap or truncate audio:
   * the second sound starts only after the first ends (or is paused by
   * mute/stop). Child input is never blocked — only audio ordering waits.
   */
  private queue: Promise<void> = Promise.resolve();
  /** Standalone (non-narration) elements currently eligible for mute/stop. */
  private standalone = new Set<HTMLAudioElement>();
  private voices: VoiceManifest;
  private random: () => number;
  private lastSpeaker: VoiceId | null = null;
  private lastCountingVoice: VoiceId | null = null;
  private lastFuelVoice: VoiceId | null = null;
  /** Bumped by cancelQueued(); calls captured under an older epoch no-op. */
  private queueEpoch = 0;
  private lastSpoken_: { id: string; voice: VoiceId } | null = null;

  constructor(
    lines: NarrationLine[],
    private opts: NarrationOptions,
    voices: VoiceManifest = EMPTY_VOICE_MANIFEST,
  ) {
    this.voices = voices;
    this.random = opts.random ?? Math.random;
    this.muted = opts.initialMuted ?? false;
    for (const line of lines) {
      this.lines.set(line.id, line);
      const el = new Audio(line.audioPath);
      el.preload = 'auto';
      el.addEventListener('ended', () => {
        this.opts.onLineEnded?.(line.id);
      });
      this.audio.set(line.id, el);
    }

    for (const childVoice of CHILD_VOICES) {
      const ids = this.voices[childVoice] ?? [];
      for (const id of ids) {
        const line = this.lines.get(id);
        if (!line) continue;
        const el = new Audio(voicePath(childVoice, line));
        el.preload = 'auto';
        el.addEventListener('ended', () => {
          this.opts.onLineEnded?.(line.id);
        });
        el.addEventListener('error', () => {
          this.failedChildElements.add(el);
        });
        this.childAudio.set(`${childVoice}:${id}`, el);
      }
    }
  }

  static async load(opts: NarrationOptions): Promise<Narration> {
    const res = await fetch('/source/narration.json', { cache: 'force-cache' });
    if (!res.ok) throw new Error(`narration.json ${res.status}`);
    const lines = (await res.json()) as NarrationLine[];

    const mergedLines = [...lines];
    try {
      const countRes = await fetch('/source/narration-count.json', { cache: 'force-cache' });
      if (countRes.ok) {
        const countData = await countRes.json();
        if (Array.isArray(countData)) {
          mergedLines.push(...(countData as NarrationLine[]));
        }
      }
    } catch {
      /* ignore failure fetching narration-count */
    }

    try {
      const fuelRes = await fetch('/source/narration-fuel.json', { cache: 'force-cache' });
      if (fuelRes.ok) {
        const fuelData = await fuelRes.json();
        if (Array.isArray(fuelData)) {
          mergedLines.push(...(fuelData as NarrationLine[]));
        }
      }
    } catch {
      /* ignore failure fetching narration-fuel */
    }

    let voiceManifest: VoiceManifest = EMPTY_VOICE_MANIFEST;
    try {
      const voiceRes = await fetch('/source/voices.json', { cache: 'force-cache' });
      if (voiceRes.ok) {
        voiceManifest = parseVoiceManifest(await voiceRes.json());
      }
    } catch {
      /* failure -> EMPTY */
    }

    return new Narration(mergedLines, opts, voiceManifest);
  }

  get muted_(): boolean {
    return this.muted;
  }

  get lastSpoken(): { id: string; voice: VoiceId } | null {
    return this.lastSpoken_;
  }

  pickCountingVoice(): VoiceId {
    const voice = pickCountingVoice(this.voices, this.lastCountingVoice, this.random);
    this.lastCountingVoice = voice;
    return voice;
  }

  pickFuelVoice(): VoiceId {
    const voice = pickFuelVoice(this.voices, this.lastFuelVoice, this.random);
    this.lastFuelVoice = voice;
    return voice;
  }

  /**
   * Invalidates every play()/playStandalone() call still waiting in the
   * serial queue (they resolve with no audio and no caption). The currently
   * playing element is NOT stopped; use stop() for that.
   */
  cancelQueued(): void {
    this.queueEpoch += 1;
  }

  setMuted(next: boolean) {
    this.muted = next;
    if (next) {
      if (this.current) {
        this.current.pause();
        this.current.currentTime = 0;
      }
      for (const el of this.standalone) el.pause();
    }
  }

  /** Play a single chime audio path outside the narration table. */
  async playStandalone(path: string): Promise<void> {
    const epoch = this.queueEpoch;
    const run = this.queue.then(() =>
      epoch === this.queueEpoch ? this.runStandalone(path) : undefined,
    );
    this.queue = run.catch(() => {});
    return run;
  }

  private async runStandalone(path: string): Promise<void> {
    if (this.muted) return;
    const el = new Audio(path);
    el.preload = 'auto';
    this.standalone.add(el);
    try {
      await el.play();
      await this.waitForEnd(el);
    } catch {
      /* Autoplay can still reject on rare device-policy grounds; the
         start-gesture unlock should prevent this on the happy path. */
    } finally {
      this.standalone.delete(el);
    }
  }

  async play(id: string, options?: { voice?: VoiceId }): Promise<void> {
    const line = this.lines.get(id);
    if (!line) throw new Error(`Unknown narration id: ${id}`);
    const epoch = this.queueEpoch;
    const run = this.queue.then(() =>
      epoch === this.queueEpoch ? this.runLine(line, id, options?.voice, epoch) : undefined,
    );
    this.queue = run.catch(() => {});
    return run;
  }

  private async runLine(
    line: NarrationLine,
    id: string,
    requestedVoice?: VoiceId,
    epoch: number = this.queueEpoch,
  ): Promise<void> {
    const avail = availableVoices(id, this.voices);
    let chosenVoice: VoiceId;
    if (requestedVoice && avail.includes(requestedVoice)) {
      chosenVoice = requestedVoice;
    } else {
      chosenVoice = pickVoice(avail, this.lastSpeaker, this.random);
    }
    this.lastSpeaker = chosenVoice;
    this.lastSpoken_ = { id, voice: chosenVoice };
    this.opts.onVoice?.(id, chosenVoice);

    const computerEl = this.audio.get(id)!;
    const childEl =
      chosenVoice !== 'computer' ? this.childAudio.get(`${chosenVoice}:${id}`) : undefined;
    const chosenEl = childEl ?? computerEl;

    if (this.current && this.current !== chosenEl) {
      this.current.pause();
      this.current.currentTime = 0;
    }
    this.current = chosenEl;
    this.opts.onCaption(line.text, id);
    if (this.muted) {
      // Captions still render; muted play is fully viable.
      this.opts.onLineEnded?.(id);
      return;
    }

    const playElement = async (el: HTMLAudioElement): Promise<void> => {
      el.currentTime = 0;
      await el.play();
      await this.waitForEnd(el);
    };

    if (childEl) {
      const alreadyFailed = this.failedChildElements.has(childEl) || Boolean(childEl.error);
      let childSucceeded = false;
      if (!alreadyFailed) {
        try {
          await playElement(childEl);
          childSucceeded = !this.failedChildElements.has(childEl);
        } catch {
          /* child play() rejected */
        }
      }

      if (!childSucceeded) {
        // A cancelQueued()/stop() while the child clip was starting aborts its
        // play(); that is not a failure, so never replay the stale line.
        if (epoch !== this.queueEpoch) return;
        // Fall back ONCE to the computer element for that line inside the same queue slot
        if (this.current === childEl) {
          childEl.pause();
          childEl.currentTime = 0;
        }
        this.current = computerEl;
        if (this.muted) {
          this.opts.onLineEnded?.(id);
          return;
        }
        try {
          await playElement(computerEl);
        } catch {
          /* If playback fails, captions still conveyed the line. */
          this.opts.onLineEnded?.(id);
        }
      }
    } else {
      try {
        await playElement(computerEl);
      } catch {
        /* If playback fails, captions still conveyed the line. */
        this.opts.onLineEnded?.(id);
      }
    }
  }

  /**
   * Resolves on natural end OR pause. The pause path matters so that
   * muting or stop() mid-line releases queued waiters instead of hanging.
   */
  private waitForEnd(el: HTMLAudioElement): Promise<void> {
    return new Promise((resolve) => {
      const done = () => {
        el.removeEventListener('ended', done);
        el.removeEventListener('pause', done);
        el.removeEventListener('error', done);
        resolve();
      };
      el.addEventListener('ended', done);
      el.addEventListener('pause', done);
      el.addEventListener('error', done);
    });
  }

  stop() {
    if (this.current) {
      this.current.pause();
      this.current.currentTime = 0;
    }
    for (const el of this.standalone) el.pause();
    this.current = null;
  }

  text(id: string): string | undefined {
    return this.lines.get(id)?.text;
  }
}
